/**
 * useRenterFileDetail — single-file detail hook (AC-2.7).
 *
 * Design decisions:
 *  - T5-34: AbortController on fetch.
 *  - T3-4: On 404, navigate to /renter/files.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getAIFileDetail, patchMatch } from 'src/services/aiFileUploadV2';
import type {
  ExtractionMatch,
  RenterFileDetail,
} from 'src/types/aiFileUploadV2';

export interface UseRenterFileDetailResult {
  detail: RenterFileDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  confirmOrEditMatch: (
    matchId: number,
    value: string
  ) => Promise<ExtractionMatch | null>;
}

export function useRenterFileDetail(
  fileId: number | null,
  { onNotFound }: { onNotFound?: () => void } = {}
): UseRenterFileDetailResult {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<RenterFileDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const onNotFoundRef = useRef(onNotFound);
  useEffect(() => {
    onNotFoundRef.current = onNotFound;
  });

  const fetchDetail = useCallback(() => {
    if (!fileId) {
      return;
    }

    // Cancel in-flight (T5-34)
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);
    setError(null);

    getAIFileDetail(fileId, signal)
      .then((data) => {
        setDetail(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const e = err as Error & { response?: { status: number } };
        if (e.name === 'CanceledError' || e.name === 'AbortError') {
          return;
        }
        // T3-4: on 404 redirect to files list (or call onNotFound if provided)
        if (e.response?.status === 404) {
          if (onNotFoundRef.current) {
            onNotFoundRef.current();
          } else {
            navigate('/renter/files', { replace: true });
          }
          return;
        }
        setError(e.message ?? 'Failed to load file detail');
        setLoading(false);
      });
  }, [fileId, navigate]);

  useEffect(() => {
    fetchDetail();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchDetail]);

  // T5-29: functional setState used above
  const confirmOrEditMatch = useCallback(
    async (matchId: number, value: string): Promise<ExtractionMatch | null> => {
      if (!fileId) {
        return null;
      }
      const ac = new AbortController();
      try {
        const updated = await patchMatch(
          fileId,
          matchId,
          { value, user_confirmed: true },
          ac.signal
        );
        setDetail((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            matches: prev.matches.map((m) => (m.id === matchId ? updated : m)),
          };
        });
        return updated;
      } catch (err: unknown) {
        const e = err as Error;
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          setError(e.message ?? 'Failed to confirm match');
        }
        return null;
      }
    },
    [fileId]
  );

  return { detail, loading, error, refetch: fetchDetail, confirmOrEditMatch };
}
